import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '#app/utils/misc.tsx';

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors outline-none disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'bg-primary text-primary-foreground hover:bg-primary/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				in_progress:
					'bg-orange-500 text-accent-foreground hover:bg-orange-400/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				muted:
					'bg-muted text-muted-foreground hover:bg-muted/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				accent:
					'bg-accent text-accent-foreground hover:bg-accent/80 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				ghost:
					'hover:bg-gray-300 focus-visible:ring-4 focus-within:ring-4 ring-ring ring-offset-2',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-10 px-4 py-2',
				wide: 'px-24 py-5',
				sm: 'h-9 rounded-md px-3',
				xs: 'h-6 rounded-md px-1',
				lg: 'h-11 rounded-md px-8',
				pill: 'px-12 py-3 leading-3',
				icon: 'h-10 w-10',
				icon_small: 'h-6 w-6',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
